import React, { createContext, useContext, useState } from "react";
import * as Yup from "yup";
import rfdc from "rfdc";
import { useTheme } from "./state/theme/theme";
import { useSettings } from "./state/settings/settings";
import setDemoBuilderPageTitle from "components/controllers/shared/analytics/demoBuilderPageTitle";
import { initialError } from "./state/initial/initial";
import webdemoValidationSchema, {
	combineSchemaObjs,
} from "../../../components/controllers/forms/custom/shared/validation/webdemoFormValidation";
import { UploadStatus } from "../../../components/controllers/customize/demo/create/formChanges/uploadStatus";
import { useGlobalContext } from "../../global/state";

// Clone function
const clone = rfdc();

type BuilderContextProps = any;

const BuilderContext = createContext({} as BuilderContextProps);

export const BuilderProvider = (props: any) => {
	let { data, state } = useGlobalContext();
	const { currentUser } = state;

	const validationSchemas = [webdemoValidationSchema];
	const schemaObj = combineSchemaObjs(validationSchemas);

	// Initial
	const initialUploadStatus: UploadStatus = {
		percentage: 0,
		message: "",
		subMessage: "",
	};
	// TODO(PWDC-24/Display Experiences Page): Change the way the data is handled (right now, refer to centralized data)
	/* About this TODO
		This is a temporary fix for new form integration
		The display plan fields used to upload the data in their component but now the form can be submitted on step 1
	*/

	const globalDataCopy = clone(data); // Needs copy, builder uses custom global object

	// Plan Data
	const displayPlans = {
		member: { ...globalDataCopy.displayPlans.member },
		employer: { ...globalDataCopy.displayPlans.employer },
	};
	const displayOtherPlans = {
		member: { ...globalDataCopy.displayOtherPlans.member },
		employer: { ...globalDataCopy.displayOtherPlans.employer },
	};
	// originalData is used to reset the plans
	const originalData: any = {
		displayPlans,
		displayOtherPlans,
	};

	const initialCustomData = clone(originalData);

	// Builder State Properties
	/*
		Every part of the builder will be managed by the builder state object (aka rn formState //TODO: Rename formState to builder)
	*/
	// Builder Data: Logos
	const [fileItem, setFileItem] = useState();
	const [imagePreview, setImagePreview] = useState();
	// Builder Data: Plan Data // TODO: Change customData to plans, setPlans
	const [customData, setCustomData] = useState(initialCustomData);

	// Builder steps
	const [step, setStepState] = useState(1);
	const setStep = (step) => {
		setStepState(step);
		setDemoBuilderPageTitle(step);
	};

	// FINAL State. Fully integrated once publish modal is removed (and interstitial is fully integrated)
	const [isSubmitted, setIsSubmitted] = useState(false);

	// Builder Upload Statuses
	const [uploadStatus, setUploadStatus] = useState(initialUploadStatus);
	const [uploadInProgress, setUploadInProgress] = useState(false); // TODO: Change to submitted, setSubmitted
	const [uploadComplete, setUploadComplete] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	// Builder Errors
	const [error, setError] = useState(initialError);
	const resetError = () => setError(clone(initialError));
	const [retryCount, setRetry] = useState(0);
	const [uploadTimedOut, setUploadTimedOut] = useState(false);
	const [uploadFail, setUploadFail] = useState(false);

	// Builder checkUploadStatus: needs jobId
	const [jobId, setJobId] = useState();

	const [publishedUrl, setPublishedUrl] = useState();

	// Builder validation schema for formik
	let schema = Yup.object();
	if (validationSchemas) {
		if (validationSchemas.length > 0)
			schema = Yup.object().shape(schemaObj);
	}

	// Builder list of data objects
	// Helper to loop over custom data obj properties that contain objects
	// The objects get stringified in preparation for form post
	// ANY NEW Objects, add here and it will be sent automatically
	const dataWithObjectTypes = [
		"displayPlans",
		"displayOtherPlans",
		"settings",
		"theme",
	];

	const formState = {
		...useTheme(),
		...useSettings(),

		// Client Logo
		fileItem,
		setFileItem,
		imagePreview,
		setImagePreview,

		// Plan Selection Data
		data: customData,
		setData: setCustomData,
		originalData,

		// Steps
		step,
		setStep,

		// Errors TODO: Clean up errors; used/used differently than expectation
		error,
		setError,
		noErrors: error.message === "",
		resetError,

		// Validation
		schema,
		schemaObj,

		// Other
		dataWithObjectTypes,
		currentUser,

		// Last state (Used in interstitial; TODO: utilize after full interstitial integration)
		isSubmitted,
		setIsSubmitted,

		// TODO: Take a look how this is being used (potentially used in multiple processes)
		// If used in multiple processes, break the other processes into it's own state to manage
		isLoading,
		setIsLoading,

		// TODO: Everything Below => To be removed after interstitial is fully integrated
		// Upload Status
		uploadInProgress,
		setUploadInProgress,
		uploadStatus,
		setUploadStatus,
		uploadComplete,
		setUploadComplete,
		jobId,
		setJobId,
		publishedUrl,
		setPublishedUrl,

		// Upload Status: Error Handling
		uploadFail,
		setUploadFail,
		uploadTimedOut,
		setUploadTimedOut,
		retryCount,
		setRetry,
	};

	// TODO: remove custom.formState later (mainly needed in legacy publish modal feature)
	const custom = { formState };

	return (
		<BuilderContext.Provider value={{ ...formState, custom }}>
			{props.children}
		</BuilderContext.Provider>
	);
};

export const useBuilderContext = () => useContext(BuilderContext);
